import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

class ErrorBoundaryInternal extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    reset = () => {
        this.setState({ hasError: false, error: null });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-background px-6">
                    <div className="max-w-md text-center">
                        <div className="mb-6 inline-flex p-4 bg-red-100 rounded-full text-red-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        </div>
                        <h1 className="font-fredoka text-4xl font-bold text-foreground mb-4">
                            Oops! Something went wrong ✨
                        </h1>
                        <p className="font-nunito text-muted-foreground mb-6">
                            Don&apos;t worry, your data is safe! Try refreshing or going back home.
                        </p>

                        {process.env.NODE_ENV === 'development' && (
                            <div className="mb-6 p-4 bg-muted rounded-xl text-left overflow-auto max-h-48 text-xs font-mono">
                                <p className="font-bold text-red-600 mb-2">{this.state.error?.toString()}</p>
                                <pre>{this.state.error?.stack}</pre>
                            </div>
                        )}

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={this.props.onReset}
                                className="w-full px-6 py-3 bg-primary text-white rounded-full font-fredoka font-semibold shadow-hard hover:shadow-hard-lg transition-all"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => {
                                    this.props.onReset();
                                    this.props.navigate('/');
                                }}
                                className="w-full px-6 py-3 bg-white border-2 border-border text-foreground rounded-full font-fredoka font-semibold hover:bg-muted transition-all"
                            >
                                Go Back Home
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const ErrorBoundary = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const errorBoundaryRef = React.useRef(null);

    React.useEffect(() => {
        if (errorBoundaryRef.current) {
            errorBoundaryRef.current.reset();
        }
    }, [location.pathname]);

    return (
        <ErrorBoundaryInternal
            ref={errorBoundaryRef}
            navigate={navigate}
            onReset={() => errorBoundaryRef.current?.reset()}
        >
            {children}
        </ErrorBoundaryInternal>
    );
};

export default ErrorBoundary;
